import { Platform } from '@angular/cdk/platform';

import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DOCUMENT, OnDestroy, OnInit, inject } from '@angular/core';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { FuseConfig } from '@fuse/types/fuse-config';
import { TranslateService } from '@ngx-translate/core';
import { locale as navigationEnglish } from 'app/core/services/left-menu/i18n/en';
import { locale as navigationFrench } from 'app/core/services/left-menu/i18n/fr';
import { locale as navigationPortuguese } from 'app/core/services/left-menu/i18n/pt';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from './core/services/auth.service';
import { ConfigurationService } from './core/services/configuration.service';
import { DatadogContextStateService } from './core/services/datadog-context-state.service';
import { AreaMenu } from './core/services/left-menu/area-menu.enum';
import { LeftMenuService } from './core/services/left-menu/left-menu.service';
import { LocalStorageService } from './core/services/local-storage.service';
import { LoggerService } from './core/services/logger.service';
import { WorkingLaboratoryService } from './core/services/prothesis-api/working-laboratory.service';
import { WorkingCustomerService } from './core/services/prothesis-customer-api/working-customer.service';
import { SettingsService } from './core/services/settings.service';
import { ThemingService } from './core/services/theme.service';
import { NavigationInterface } from './layout/components/navigation/models/navigation-interface';
import { Area } from './shared/enums/area.enum';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-root',
  styleUrls: ['./app.component.scss'],
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnDestroy, OnInit {
  public fuseConfig: FuseConfig;
  public initErrorFlag = false;

  private _authService = inject(AuthService);
  private _changeDetectorRef = inject(ChangeDetectorRef);
  private _configurationService = inject(ConfigurationService);
  private _contextStateService = inject(DatadogContextStateService);
  private _document = inject<Document>(DOCUMENT);
  private _fuseConfigService = inject(FuseConfigService);
  private _fuseNavigationService = inject(FuseNavigationService);
  private _fuseSplashScreenService = inject(FuseSplashScreenService);
  private _fuseTranslationLoaderService = inject(FuseTranslationLoaderService);
  private _http = inject(HttpClient);
  private _leftMenuService = inject(LeftMenuService);
  private _localStorageService = inject(LocalStorageService);
  private _loggerService = inject(LoggerService);
  private _platform = inject(Platform);
  private _themeSubscription: Subscription;
  private _themingService = inject(ThemingService);
  private _translateService = inject(TranslateService);
  private _unsubscrbieConfig$: Subject<void> = new Subject<void>();
  private _unsubscribeAll$: Subject<void> = new Subject<void>();
  private _workingCustomerService = inject(WorkingCustomerService);
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public constructor() {
    const _settingService = inject(SettingsService);

    this._configureWithNewConfigApi();

    // Register the navigation to the service
    this._fuseNavigationService.register('main', new Array<NavigationInterface>());

    // Set the main navigation as our current navigation
    this._fuseNavigationService.setCurrentNavigation('main');

    // Add languages
    this._translateService.addLangs(_settingService.getSupportedLanguageIds());

    // Set the fallback language
    this._translateService.setFallbackLang(_settingService.getDefaultLanguage());

    // Set the navigation translations
    this._fuseTranslationLoaderService.loadTranslations(navigationFrench, navigationEnglish, navigationPortuguese);

    // Use a language
    this._translateService.use(_settingService.getLanguage());

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix Start
     * ----------------------------------------------------------------------------------------------------
     */

    /**
     * If you are using a language other than the default one, i.e. Turkish in this case,
     * you may encounter an issue where some of the components are not actually being
     * translated when your app first initialized.
     *
     * This is related to ngxTranslate module and below there is a temporary fix while we
     * are moving the multi language implementation over to the Angular's core language
     * service.
     */

    // Set the default language to 'en' and then back to 'tr'.
    // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
    // been selected and there is no way to force it, so we overcome the issue by switching
    // the default language back and forth.
    //  setTimeout(() => {
    //     this._translateService.setDefaultLang('en');
    //     this._translateService.setDefaultLang('tr');
    //  });

    /**
     * ----------------------------------------------------------------------------------------------------
     * ngxTranslate Fix End
     * ----------------------------------------------------------------------------------------------------
     */

    // Add is-mobile class to the body if the platform is mobile
    if (this._platform.ANDROID || this._platform.IOS) {
      this._document.body.classList.add('is-mobile');
    }
  }

  public buildNavigation(): void {
    // Set the navigation menu
    const isAdminOrSuperAdminFlag: boolean = this._authService.isAdminOrSuperAdminFlag;
    const areaMenus: Array<AreaMenu> = [];
    if (isAdminOrSuperAdminFlag) {
      areaMenus.push(AreaMenu.Admin);
    } else if (this._authService.area === Area.Laboratory && this._authService.allowedLaboratoryUuids) {
      areaMenus.push(AreaMenu.Laboratory);
    } else if (this._authService.area === Area.Customer && this._authService.allowedCustomerUuids) {
      if (this._workingCustomerService.customerMemberFlag || !this._workingLaboratoryService.laboratoryRateAutoPublishFlag) {
        areaMenus.push(AreaMenu.CustomerMember);
      } else {
        areaMenus.push(AreaMenu.CustomerAndCustomerGroup);
      }
    } else {
      areaMenus.push(AreaMenu.NoArea);
    }
    const navigation = this._leftMenuService.getNavigation(
      areaMenus,
      null,
      isAdminOrSuperAdminFlag,
      this._workingLaboratoryService.laboratoryName,
    );
    this._fuseNavigationService.unregister('main');
    this._fuseNavigationService.register('main', navigation);

    // Try exploring
    if (isAdminOrSuperAdminFlag) {
      this._workingLaboratoryService.tryExploring();
    }

    if (isAdminOrSuperAdminFlag || this._authService.area === Area.Laboratory) {
      this._workingCustomerService.tryExploring();
    }
  }

  public ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._themeSubscription.unsubscribe();

    this._unsubscrbieConfig$.next();
    this._unsubscrbieConfig$.complete();

    this._unsubscribeAll$.next();
    this._unsubscribeAll$.complete();
  }

  public ngOnInit(): void {
    const colorThemeString = this._localStorageService.read<string>('THEME');
    if (colorThemeString) {
      const colorTheme = JSON.parse(colorThemeString);
      this._fuseConfigService.config = colorTheme;
    }

    this._authService.initErrorFlag$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((initErrorFlag) => {
      this.initErrorFlag = initErrorFlag;
      this._changeDetectorRef.markForCheck();
    });

    this._authService.isDoneLoading$.pipe(takeUntil(this._unsubscribeAll$)).subscribe((isDoneLoadingFlag) => {
      if (isDoneLoadingFlag) {
        this._contextStateService.setUserUuid(this._authService.userUuid);
        this._authService.areaConfiguredFlag$.pipe(takeUntil(this._unsubscribeAll$)).subscribe(() => this.buildNavigation());
      }
    });

    // Subscribe to config changes
    this._fuseConfigService.config.pipe(takeUntil(this._unsubscrbieConfig$)).subscribe((config) => {
      this.fuseConfig = config;

      // Boxed
      if (this.fuseConfig.layout.width === 'boxed') {
        this._document.body.classList.add('boxed');
      } else {
        this._document.body.classList.remove('boxed');
      }

      console.info(`AppComponent: fuse config changed : ${config.colorTheme}`);
      if (config.colorTheme === 'system') {
        if (!this._themeSubscription || this._themeSubscription.closed) {
          console.info('AppComponent: Selected theme is System, subscribing to ThemeService.theme');
          this._themeSubscription = this._themingService.theme.subscribe((theme) => {
            this._applyTheme(theme);
          });
        }
      } else {
        if (this._themeSubscription && !this._themeSubscription.closed) {
          this._themeSubscription.unsubscribe();
        }

        this._applyTheme(this.fuseConfig.colorTheme);
      }

      this._changeDetectorRef.markForCheck();
    });
  }

  public onRetry(): void {
    window.location.reload();
  }

  private _applyTheme(theme: string) {
    for (const className of this._document.body.classList) {
      if (className.startsWith('theme-')) {
        this._document.body.classList.remove(className);
      }
    }

    this._document.body.classList.add(theme);
  }

  private _configureWithNewConfigApi(): void {
    this._http.get('/Configuration').subscribe((res) => {
      this._authService.initialize(res['identityServerAddress']);
      this._loggerService.initialize(res['env']);
      this._configurationService.Env = res['env'];
      this._configurationService.Feature1EnabledFlag = res['feature1EnabledFlag'];
      this._configurationService.Feature2EnabledFlag = res['feature2EnabledFlag'];
      this._configurationService.Feature3EnabledFlag = res['feature3EnabledFlag'];
      this._configurationService.Feature4EnabledFlag = res['feature4EnabledFlag'];
      this._configurationService.Feature5EnabledFlag = res['feature5EnabledFlag'];
      this._configurationService.IdentityServerAddress = res['identityServerAddress'];
      this._configurationService.ProthesisApiAddress = res['prothesisApiAddress'];
      this._configurationService.ProthesisCustomerApiAddress = res['prothesisCustomerApiAddress'];
      this._configurationService.ProthesisIdentityApiAddress = res['prothesisIdentityApiAddress'];
      this._configurationService.StripePublishableKey = res['stripePublishableKey'];
      this._authService.runInitialLoginSequence();
    });
  }
}
